import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import BackButton from "@/components/BackButton"
import { Button } from "@/components/chadcn/Button"
import { Label } from "@/components/chadcn/Label"
import { TextareaInput } from "@/components/InputFields"
import Spinner from "@/components/Spinner"

const INFO_LABEL =
  "Anything else about this measurement that you would like to share with your care team?"
const INFO_PLACEHOLDER =
  "[e.g. I just took my medication; I just went for a long walk; my cat has zoomies]"
const SUBMIT_BUTTON = "Finished! Submit all answers"

const formSchema = z.object({
  notes: z.string().max(400, "Please limit your input to a maximum of 400 characters"),
})

export type NoteScreenValues = z.infer<typeof formSchema>

type Props = {
  context: string
  initialValues?: NoteScreenValues
  isLoading?: boolean
  onSubmit: (values: NoteScreenValues) => void
  onBack?: () => void
}

const NoteScreenStep = ({
  context,
  initialValues = { notes: "" },
  isLoading = false,
  onSubmit,
  onBack,
}: Props) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NoteScreenValues>({
    mode: "onSubmit",
    resolver: zodResolver(formSchema),
    defaultValues: initialValues,
  })

  return (
    <form
      className="flex min-h-[calc(100vh-32px)] flex-col gap-4"
      onSubmit={handleSubmit(onSubmit)}
    >
      {onBack && <BackButton customClickHandler={onBack} />}
      <h2 className="text-h2 text-primary-contrast">{context}</h2>

      <div className="mt-6 flex flex-1 flex-col gap-2">
        <Label htmlFor="notes" className="text-h4">{INFO_LABEL}</Label>
        <TextareaInput
          id="notes"
          placeholder={INFO_PLACEHOLDER}
          isError={!!errors.notes}
          disabled={isLoading}
          className="min-h-[12rem] flex-1 resize-none"
          {...register("notes")}
        />
        {errors.notes && (
          <p className="text-sm text-error">{errors.notes.message}</p>
        )}
      </div>

      <Button
        type="submit"
        disabled={isLoading}
        className="flex w-full gap-3 py-2 text-base"
      >
        {isLoading && <Spinner />}
        {SUBMIT_BUTTON}
      </Button>
    </form>
  )
}

export default NoteScreenStep
